// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The narration file behind one plan approval.
 *
 * Deliberately not `usePlanDetail`, which also carries the approval flow's
 * mutation machinery and forks the shared `['hitl-approvals', …]` cache.
 *
 * `staleTime: Infinity` — a narration is written once at publish and never
 * edited; the Live view's freshness comes from the telemetry poll.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchPlanFile } from '../services/planService';
import type { PlanFile } from '../types';

export interface UsePlanNarrationResult {
  planFile: PlanFile | null;
  loading: boolean;
  error: string | null;
}

export function usePlanNarration(
  siteId?: string | null,
  storeKey?: string | null
): UsePlanNarrationResult {
  const enabled = Boolean(siteId && storeKey);
  const query = useQuery({
    queryKey: ['plan-narration', siteId ?? '', storeKey ?? ''] as const,
    queryFn: () => fetchPlanFile(siteId as string, storeKey as string),
    enabled,
    staleTime: Infinity,
  });

  return {
    planFile: query.data ?? null,
    loading: enabled && query.isPending,
    error: query.isError ? "Couldn't load this plan's details." : null,
  };
}
